<template>
  <div>
    <ValidationObserver v-slot="{ invalid }">
      <div class="row">
        <!-- SETTINGS CARD SECTION -->
        <div class="col-12 col-xl-3">
          <document-settings
            :title="$t('message.recurringInvoiceDetails')"
            form
          >
            <b-form slot="body">
              <ValidationProvider rules="required" v-slot="{ errors }">
                <fg-input
                  :label="$t('message.invoiceRecipient')"
                  :error="errors[0]"
                >
                  <b-form-select
                    v-model="invoice.to"
                    :options="customersOptions"
                  >
                  </b-form-select>
                </fg-input>
              </ValidationProvider>
              <fg-input :label="$t('message.recurringInvoiceFirstIssue')">
                <vue-ctk-date-time-picker
                  v-model="invoice.sentDate"
                  format="YYYY-MM-DD"
                  formatted="DD/MM/YYYY"
                  :placeholder="$t('message.chooseDate')"
                  :only-date="true"
                  :no-label="true"
                  required
                >
                </vue-ctk-date-time-picker>
              </fg-input>
              <ValidationProvider
                rules="required|min_value:1"
                v-slot="{ errors }"
              >
                <fg-input :label="$t('message.recurringInvoiceTimeToPay')">
                  <b-form-input
                    placeholder="30"
                    v-model="invoice.paymentDays"
                    :error="errors[0]"
                  />
                </fg-input>
              </ValidationProvider>
              <fg-input :label="$t('message.recurringInvoiceRepeatEvery')">
                <b-form-select
                  class="select-primary"
                  v-model="invoiceSettings.recurringDuration"
                  :options="recurringDurationOptions"
                >
                </b-form-select>
              </fg-input>
              <fg-input :label="$t('message.recurringInvoiceEnding')">
                <b-form-select
                  v-model="invoiceSettings.recurringEndType"
                  :options="recurringEndTypeOptions"
                >
                </b-form-select>
              </fg-input>
              <fg-input
                v-if="invoiceSettings.recurringEndType === 'AFTER_OCCURRENCES'"
              >
                <b-form-input
                  placeholder="10"
                  v-model="invoiceSettings.recurringEndAfterOccurrences"
                >
                  <template slot="append">
                    {{
                      $t("message.recurringInvoiceOccurrences").toLowerCase()
                    }}
                  </template>
                </b-form-input>
              </fg-input>
              <fg-input
                v-else-if="invoiceSettings.recurringEndType === 'ON_DATE'"
              >
                <vue-ctk-date-time-picker
                  v-model="invoiceSettings.recurringEndOnDate"
                  format="YYYY-MM-DD"
                  formatted="DD/MM/YYYY"
                  :placeholder="$t('message.recurringInvoicePickADay')"
                  :only-date="true"
                  :no-label="true"
                  required
                >
                </vue-ctk-date-time-picker>
              </fg-input>
            </b-form>
            <div slot="actions">
              <hr />
              <div class="d-none d-xl-block">
                <n-button
                  type="primary"
                  round
                  native-type="submit"
                  @click.native.prevent="submit"
                  :disabled="invalid || !!loader"
                  >{{ $t("message.submit") }}</n-button
                >
                <n-button
                  round
                  simple
                  @click.native.prevent="goBack('/invoices')"
                  >{{ $t("message.cancel") }}</n-button
                >
              </div>
            </div>
          </document-settings>
        </div>
        <!--  INVOICE SECTION -->
        <div class="col-12 col-xl-9">
          <invoice-form
            :invoice="invoice"
            @change="onInvoiceChange"
            recurring
          ></invoice-form>
        </div>
      </div>
      <!--  ADDITIONAL SAVE BUTTON FOR MOBILE -->
      <div class="row">
        <div class="col d-block d-xl-none">
          <card class="d-flex align-items-center">
            <n-button
              type="primary"
              round
              native-type="submit"
              @click.native.prevent="submit"
              :disabled="invalid || !!loader"
              >{{ $t("message.submit") }}</n-button
            >
            <n-button
              round
              simple
              @click.native.prevent="goBack('/invoices')"
              >{{ $t("message.cancel") }}</n-button
            >
          </card>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import _ from "lodash";
import { Radio, DocumentSettings } from "@/components";
import { formatMoney, dateToHuman } from "@/helpers/helpers.js";
import { DEFAULT_INVOICE } from "@/store/accounting/invoices";
import InvoiceForm from "../invoices/InvoiceForm";
import { goBack } from "@/mixins";

export default {
  mixins: [goBack],
  components: {
    InvoiceForm,
    Radio,
    DocumentSettings
  },
  data() {
    return {
      loader: null,
      recurringDurationOptions: [
        {
          value: "DAY",
          text: this.$t("message.recurringInvoiceDay")
        },
        {
          value: "WEEK",
          text: this.$t("message.recurringInvoiceWeek")
        },
        {
          value: "MONTH",
          text: this.$t("message.recurringInvoiceMonth")
        },
        {
          value: "YEAR",
          text: this.$t("message.recurringInvoiceYear")
        }
      ],
      recurringEndTypeOptions: [
        {
          value: "NEVER",
          text: this.$t("message.recurringInvoiceEndTypeNever")
        },
        {
          value: "AFTER_OCCURRENCES",
          text: this.$t("message.recurringInvoiceEndTypeAfterOccurrences")
        },
        {
          value: "ON_DATE",
          text: this.$t("message.recurringInvoiceEndTypeOnDate")
        }
      ],
      invoiceSettings: {
        recurringDuration: "DAY",
        recurringEndType: "NEVER",
        recurringEndOnDate: null,
        recurringEndAfterOccurrences: null
      },
      invoice: {
        ..._.cloneDeep(DEFAULT_INVOICE),
        sentDate: moment().format("YYYY-MM-DD"),
        dueDate: null
      }
    };
  },
  methods: {
    formatMoney,
    ...mapActions([
      "fetchUserSettings",
      "fetchCustomers",
      "fetchInvoiceNumberHistory",
      "startPollingInvoiceNumberHistory",
      "stopPollingInvoiceNumberHistory",
      "addRecurringInvoice"
    ]),
    setInvoiceDaysToPay(daysToPay) {
      if (!daysToPay) return;
      this.invoice.paymentDays = daysToPay;
    },
    onInvoiceChange(invoice) {
      this.invoice = invoice;
    },
    async submit() {
      const { invoiceSettings } = this;
      const { recurringDuration, recurringEndType } = invoiceSettings;
      const invoice = {
        ..._.omit(this.invoice, "number"),
        status: "ACTIVE",
        nextInvoiceDate: this.invoice.sentDate,
        recurringConfig: {
          daily: recurringDuration === "DAY",
          weekly: recurringDuration === "WEEK",
          monthly: recurringDuration === "MONTH",
          yearly: recurringDuration === "YEAR"
        }
      };

      if (recurringEndType === "ON_DATE") {
        invoice.endDate = invoiceSettings.recurringEndOnDate;
      }

      if (recurringEndType === "AFTER_OCCURRENCES") {
        invoice.totalOccurrences = invoiceSettings.recurringEndAfterOccurrences;
      }

      this.loader = this.$loading.show();

      try {
        await this.addRecurringInvoice(invoice);
        this.$router.push("/invoices");
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.submitRecurringInvoiceSuccess")
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text:
            this.$t("message.submitRecurringInvoiceFailure") +
            ` ${error.message}.`
        });
      } finally {
        this.loader.hide();
      }
    },
    selectCustomerFromRouteQuery() {
      const { customer_id } = this.$route.query;

      if (!customer_id) return;

      const customer = _.find(this.customers, ["_id", customer_id]);

      if (customer) this.invoice.to = customer;
    }
  },
  computed: {
    ...mapGetters(["userSettings", "customers", "nextInvoiceNumber"]),
    customersOptions() {
      return [
        {
          value: {
            companyName: null,
            emailAddress: null,
            vatNumber: null,
            address: {
              line1: null,
              line2: null,
              district: null,
              region: null,
              country: null,
              postcode: null
            }
          },
          text: "Select a Customer",
          disabled: true
        },
        ...this.customers.map(customer => ({
          value: customer,
          text: customer.companyName
        }))
      ];
    },
    dueDate() {
      return moment(this.invoice.sentDate)
        .add(this.invoice.paymentDays, "days")
        .format("YYYY-MM-DD");
    }
  },
  filters: {
    dateToHuman
  },
  created() {
    this.fetchUserSettings();
    this.fetchCustomers().then(this.selectCustomerFromRouteQuery);

    const cachedInvoice = this.$localStorage.get("recurringInvoice");

    if (cachedInvoice) {
      this.invoice = cachedInvoice;
    }

    this.invoiceSettings =
      this.$localStorage.get("invoiceSettings") || this.invoiceSettings;
    this.invoice = this.$localStorage.get("invoice") || this.invoice;
  },
  destroyed() {
    this.$localStorage.remove("invoiceSettings");
    this.$localStorage.remove("invoice");
  },
  watch: {
    nextInvoiceNumber: {
      immediate: true,
      handler(value) {
        this.invoice.number = value;
        this.invoice.paymentReference = value;
      }
    },
    userSettings: {
      immediate: true,
      handler(value) {
        this.invoice.from = { ...value.company };
        this.invoice.items = _.map(this.invoice.items, item => ({
          ...item,
          vat: item.vat || value.invoicing.defaultVatRate
        }));
        this.setInvoiceDaysToPay(value.invoicing.defaultDaysToPay);
      }
    },
    dueDate: {
      immediate: true,
      handler(value) {
        this.invoice.dueDate = value;
      }
    },
    invoiceSettings: {
      deep: true,
      handler(invoiceSettings) {
        this.$localStorage.set("invoiceSettings", invoiceSettings);
      }
    },
    invoice: {
      deep: true,
      handler(invoice) {
        this.$localStorage.set("invoice", invoice);
      }
    }
  }
};
</script>
<style lang="scss" scoped></style>
